"use client";

import {
  Box,
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Paper,
  Grid,
  SimpleGrid,
  RingProgress,
  FileButton,
  Loader,
} from "@mantine/core";
import { IconUpload, IconCheck, IconX, IconArrowRight, IconCamera, IconSparkles, IconPalette, IconShirt, IconMessageChatbot, IconRefresh } from "@tabler/icons-react";
import { useState } from "react";
import Link from "next/link";

type OutfitCheckResult = {
  score: number;
  verdict: string;
  occasion: string;
  whatWorks: string[];
  toImprove: string[];
  colorHarmony: { score: number; note: string };
  fitNote: string;
  quickUpgrades: string[];
};

const scoreColor = (score: number) => (score >= 8 ? "#10B981" : score >= 6 ? "#F59E0B" : "#EF4444");

export default function OutfitCheckContent() {
  const [imageData, setImageData] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<OutfitCheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = (file: File | null) => {
    if (!file) return;
    setError(null);
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      const data = reader.result as string;
      setImageData(data);
      runCheck(data);
    };
    reader.readAsDataURL(file);
  };

  const runCheck = async (data: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/outfit-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageData: data }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || "Outfit check failed — please try again.");
      }
      setResult(json.result as OutfitCheckResult);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Outfit check failed — please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setImageData(null);
    setResult(null);
    setError(null);
  };

  const upsells = [
    {
      icon: <IconPalette size={22} color="#7C3AED" />,
      title: "AI Color Analysis",
      body: "We checked how your outfit's colors work together — but are they YOUR colors? Find your exact seasonal palette.",
      cta: "Find my colors",
      href: "/ai-color-analysis",
    },
    {
      icon: <IconShirt size={22} color="#7C3AED" />,
      title: "AI Outfit Generator",
      body: "Skip the guesswork: get complete outfits generated on your own photo, in colors picked for you.",
      cta: "Generate my outfits",
      href: "/ai-outfit-generator",
    },
    {
      icon: <IconMessageChatbot size={22} color="#7C3AED" />,
      title: "Your AI Stylist",
      body: "Questions about what to wear? Chat with a personal AI stylist who knows your palette and style.",
      cta: "Meet your stylist",
      href: "/style-club",
    },
  ];

  return (
    <Box py={{ base: "md", md: "2.5rem" }} pb={{ base: "3rem", md: "5rem" }}>
      <Container size="md">
        <Stack gap="xl">

          {/* HERO */}
          <Stack gap={8} align="center" ta="center">
            <Text fz={12} fw={700} tt="uppercase" style={{ color: "#6D28D9", letterSpacing: 2 }}>
              Free AI fit check
            </Text>
            <Title order={1} fz={{ base: "2rem", md: "2.8rem" }} fw={600} style={{ color: "#111827" }}>
              Outfit Check
            </Title>
            <Text fz={{ base: "sm", md: "md" }} maw={560} style={{ color: "#64748B", lineHeight: 1.65 }}>
              Upload a photo of your outfit and get an honest AI review in seconds — what works, what to fix,
              whether the colors play well together, and two quick upgrades that lift the whole look.
            </Text>
          </Stack>

          {/* UPLOAD / RESULT */}
          {!result && !loading && (
            <Paper
              radius={20}
              p={{ base: "lg", md: "2rem" }}
              style={{ border: "2px dashed #C4B5FD", background: "#FAF5FF", textAlign: "center" }}
            >
              <Stack gap="md" align="center">
                <Box
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    background: "#EDE9FE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconCamera size={30} color="#7C3AED" />
                </Box>
                <Box>
                  <Text fw={600} style={{ color: "#1E293B" }}>Upload your outfit photo</Text>
                  <Text fz="sm" mt={4} style={{ color: "#64748B" }}>
                    Full-body or mirror selfie · your photo is never stored
                  </Text>
                </Box>
                <FileButton onChange={handleFile} accept="image/png,image/jpeg,image/webp">
                  {(props) => (
                    <Button
                      {...props}
                      size="lg"
                      radius="md"
                      leftSection={<IconUpload size={18} />}
                      styles={{
                        root: {
                          background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
                          border: "none",
                        },
                      }}
                    >
                      Check my outfit — free
                    </Button>
                  )}
                </FileButton>
                <Text fz="xs" style={{ color: "#94A3B8" }}>5 free checks per day · no signup</Text>
                {error && (
                  <Text fz="sm" style={{ color: "#DC2626" }}>{error}</Text>
                )}
              </Stack>
            </Paper>
          )}

          {loading && (
            <Paper radius={20} p="2rem" style={{ border: "1px solid #EEF0F4", textAlign: "center" }}>
              <Stack gap="md" align="center">
                {imageData && (
                  <Box style={{ width: 140, borderRadius: 12, overflow: "hidden" }}>
                    <img src={imageData} alt="" style={{ width: "100%", display: "block" }} />
                  </Box>
                )}
                <Loader color="violet" />
                <Text fz="sm" style={{ color: "#64748B" }}>Checking your outfit…</Text>
              </Stack>
            </Paper>
          )}

          {result && (
            <Stack gap="lg">
              {/* Score card */}
              <Paper radius={20} p={{ base: "md", md: "xl" }} style={{ border: "1px solid #EEF0F4", boxShadow: "0 8px 30px rgba(15,23,42,0.05)" }}>
                <Grid gutter="lg" align="center">
                  <Grid.Col span={{ base: 12, xs: 4 }}>
                    <Stack gap={8} align="center">
                      {imageData && (
                        <Box style={{ width: "100%", maxWidth: 180, borderRadius: 12, overflow: "hidden" }}>
                          <img src={imageData} alt="Your outfit" style={{ width: "100%", display: "block" }} />
                        </Box>
                      )}
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, xs: 8 }}>
                    <Group gap="lg" align="center" wrap="nowrap">
                      <RingProgress
                        size={96}
                        thickness={9}
                        roundCaps
                        sections={[{ value: result.score * 10, color: scoreColor(result.score) }]}
                        label={
                          <Text ta="center" fz={24} fw={700} style={{ color: "#1E293B" }}>
                            {result.score}
                          </Text>
                        }
                      />
                      <Box>
                        <Text fz={{ base: "md", md: "lg" }} fw={700} style={{ color: "#1E293B", lineHeight: 1.3 }}>
                          {result.verdict}
                        </Text>
                        <Text fz="sm" mt={4} style={{ color: "#64748B" }}>
                          Best for: {result.occasion}
                        </Text>
                      </Box>
                    </Group>

                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mt="md">
                      <Box>
                        <Text fz="xs" fw={700} tt="uppercase" mb={6} style={{ color: "#059669", letterSpacing: 0.5 }}>
                          What works
                        </Text>
                        <Stack gap={5}>
                          {result.whatWorks.map((item, i) => (
                            <Group key={i} gap={6} wrap="nowrap" align="flex-start">
                              <IconCheck size={14} color="#10B981" style={{ marginTop: 3, flexShrink: 0 }} />
                              <Text fz="sm" style={{ color: "#374151", lineHeight: 1.45 }}>{item}</Text>
                            </Group>
                          ))}
                        </Stack>
                      </Box>
                      <Box>
                        <Text fz="xs" fw={700} tt="uppercase" mb={6} style={{ color: "#D97706", letterSpacing: 0.5 }}>
                          To improve
                        </Text>
                        <Stack gap={5}>
                          {result.toImprove.map((item, i) => (
                            <Group key={i} gap={6} wrap="nowrap" align="flex-start">
                              <IconX size={14} color="#F59E0B" style={{ marginTop: 3, flexShrink: 0 }} />
                              <Text fz="sm" style={{ color: "#374151", lineHeight: 1.45 }}>{item}</Text>
                            </Group>
                          ))}
                        </Stack>
                      </Box>
                    </SimpleGrid>
                  </Grid.Col>
                </Grid>
              </Paper>

              {/* Color harmony + the color-analysis upsell hook */}
              <Paper radius={16} p="md" style={{ border: "1px solid #E9D5FF", background: "linear-gradient(180deg,#FFFFFF,#FAF5FF)" }}>
                <Group justify="space-between" align="flex-start" gap="md" wrap="wrap">
                  <Box style={{ flex: 1, minWidth: 240 }}>
                    <Group gap={8}>
                      <IconPalette size={18} color="#7C3AED" />
                      <Text fz="sm" fw={700} style={{ color: "#1E293B" }}>
                        Color harmony: {result.colorHarmony.score}/10
                      </Text>
                    </Group>
                    <Text fz="sm" mt={6} style={{ color: "#475569", lineHeight: 1.6 }}>
                      {result.colorHarmony.note}
                    </Text>
                    <Text fz="sm" mt={8} fw={600} style={{ color: "#6D28D9" }}>
                      These colors work together — but are they YOUR best colors? That depends on your skin tone,
                      eyes and hair.
                    </Text>
                  </Box>
                  <Button
                    component={Link}
                    href="/ai-color-analysis"
                    radius="md"
                    rightSection={<IconArrowRight size={16} />}
                    styles={{ root: { background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)", border: "none" } }}
                  >
                    Get my color analysis
                  </Button>
                </Group>
              </Paper>

              {/* Fit + quick upgrades */}
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <Paper radius={16} p="md" style={{ border: "1px solid #EEF0F4" }}>
                  <Text fz="xs" fw={700} tt="uppercase" mb={6} style={{ color: "#64748B", letterSpacing: 0.5 }}>
                    Fit & silhouette
                  </Text>
                  <Text fz="sm" style={{ color: "#374151", lineHeight: 1.55 }}>{result.fitNote}</Text>
                </Paper>
                <Paper radius={16} p="md" style={{ border: "1px solid #EEF0F4" }}>
                  <Text fz="xs" fw={700} tt="uppercase" mb={6} style={{ color: "#64748B", letterSpacing: 0.5 }}>
                    <IconSparkles size={12} style={{ marginRight: 4 }} />
                    Quick upgrades
                  </Text>
                  <Stack gap={5}>
                    {result.quickUpgrades.map((item, i) => (
                      <Group key={i} gap={6} wrap="nowrap" align="flex-start">
                        <Text fz="sm" fw={700} style={{ color: "#7C3AED" }}>{i + 1}.</Text>
                        <Text fz="sm" style={{ color: "#374151", lineHeight: 1.45 }}>{item}</Text>
                      </Group>
                    ))}
                  </Stack>
                </Paper>
              </SimpleGrid>

              <Button variant="default" radius="md" leftSection={<IconRefresh size={16} />} onClick={reset} mx="auto">
                Check another outfit
              </Button>
            </Stack>
          )}

          {/* UPSELLS */}
          <Box>
            <Title order={2} fz={{ base: "1.2rem", md: "1.5rem" }} fw={600} ta="center" mb="md" style={{ color: "#1E293B" }}>
              Go beyond the fit check
            </Title>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
              {upsells.map((u, i) => (
                <Paper key={i} radius={16} p="md" style={{ border: "1px solid #EEF0F4", display: "flex", flexDirection: "column" }}>
                  <Box
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 12,
                      background: "#F5F3FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {u.icon}
                  </Box>
                  <Text fw={700} mt={10} style={{ color: "#1E293B" }}>{u.title}</Text>
                  <Text fz="sm" mt={4} style={{ color: "#64748B", lineHeight: 1.55, flex: 1 }}>{u.body}</Text>
                  <Button
                    component={Link}
                    href={u.href}
                    variant="light"
                    color="violet"
                    radius="md"
                    mt={12}
                    rightSection={<IconArrowRight size={14} />}
                  >
                    {u.cta}
                  </Button>
                </Paper>
              ))}
            </SimpleGrid>
          </Box>

          {/* SEO CONTENT */}
          <Stack gap="lg" maw={720} mx="auto">
            <Box>
              <Title order={2} fz={{ base: "1.2rem", md: "1.4rem" }} fw={600} mb={8} style={{ color: "#1E293B" }}>
                What is an outfit check?
              </Title>
              <Text fz="sm" style={{ color: "#475569", lineHeight: 1.7 }}>
                An outfit check — or <strong>fit check</strong> — is a quick second opinion on what you're wearing before
                you head out. Do the pieces work together? Do the colors clash? Is the silhouette balanced? Friends are
                nice about it; our AI outfit checker is honest about it. Upload a photo and you'll get a score, specific
                strengths, and the exact tweaks that would lift the look.
              </Text>
            </Box>
            <Box>
              <Title order={2} fz={{ base: "1.2rem", md: "1.4rem" }} fw={600} mb={8} style={{ color: "#1E293B" }}>
                Rate my outfit: how the score works
              </Title>
              <Text fz="sm" style={{ color: "#475569", lineHeight: 1.7 }}>
                The AI rates your outfit out of 10 across color harmony, fit and proportions, cohesion, and
                occasion-appropriateness. Most well-put-together outfits land between 6 and 8 — a 9 or 10 means the
                colors, silhouette, and styling genuinely sing together. The goal isn't the number: it's the two or
                three concrete changes that move it up.
              </Text>
            </Box>
            <Box>
              <Title order={2} fz={{ base: "1.2rem", md: "1.4rem" }} fw={600} mb={8} style={{ color: "#1E293B" }}>
                Does my outfit match — and does it match <em>me</em>?
              </Title>
              <Text fz="sm" style={{ color: "#475569", lineHeight: 1.7 }}>
                Those are two different questions. An outfit can be perfectly coordinated and still wash you out,
                because the colors don't suit your skin's undertone. That's what a{" "}
                <Link href="/ai-color-analysis" style={{ color: "#6D28D9", fontWeight: 600 }}>
                  personal color analysis
                </Link>{" "}
                answers: your exact seasonal palette — the 30+ colors that make you look healthier and more awake, and
                the ones to avoid. Pair it with the outfit check and you'll know your look works from both angles.
              </Text>
            </Box>
          </Stack>

        </Stack>
      </Container>
    </Box>
  );
}
